import Container from '@brands/Components/Container/Container';
import Sidenav from '@brands/Dashboard/Sidenav/Sidenav';
import useWindowSize from '@brands/hooks/useWindowSize';
import React, { FC } from 'react';
import { AiOutlineQuestionCircle } from 'react-icons/ai';

import styles from './styles.module.scss';

const ConsentPolicy: FC = () => {
  const screenSize = useWindowSize();
  return (
    <>
      <Sidenav isVisibleOnMobile hideDefaultOptions authLoog>
        <a
          href="https://dentistryone.zendesk.com/hc/en-us/articles/11914427760151-How-to-change-your-password"
          target="_blank"
          className={styles.sidenavContainer}
          rel="noreferrer"
          style={{ textDecoration: 'none' }}
        >
          <div className={styles.sidenavToggleText}>Help</div>
          <div className={styles.sidenavToggle}>
            <AiOutlineQuestionCircle />
          </div>
        </a>
      </Sidenav>
      <Container
        patientDashboard
        childClassName={styles.dashboard}
        containerStyle={{
          justifyContent: screenSize.width >= 1289 ? 'center' : 'start',
          overflowY: screenSize.width >= 1289 ? 'hidden' : 'scroll',
        }}
      >
        <div className={styles.tOSBody}>
          <div id="content" className={styles.tOSBodySiteContent}>
            <main id="primary" className="site-main">
              <article id="post-929" className="post-929 page type-page status-publish hentry">
                <div className="entry-content">
                  <h2 className="wp-block-heading" data-block-type="core">
                    Consent to Care for a Teledentistry Visit
                  </h2>

                  <p>
                    This form helps you understand what to expect from Dentistry.One's teledentistry services. First,
                    take a moment to read the&nbsp;
                    <a href={`${window.location.origin}/terms-of-service`}>terms of service</a>. It is important to read
                    this before your visit.
                  </p>

                  <h3 className="wp-block-heading" data-block-type="core">
                    <strong>What is teledentistry?</strong>
                  </h3>
                  <ul data-block-type="core">
                    <li data-block-type="core">
                      ● Teledentistry lets you talk to dentists without going to their office. You can have your
                      appointment from home or anywhere comfortable.
                    </li>
                  </ul>

                  <h3 className="wp-block-heading" data-block-type="core">
                    <strong>How do I use teledentistry?</strong>
                  </h3>

                  <ul data-block-type="core">
                    <li data-block-type="core">
                      ● You can see and talk to a dentist using your smartphone, computer, or tablet with video.
                    </li>

                    <li data-block-type="core">
                      ● You can also ask questions by messaging the dentist without having a video visit.
                    </li>
                  </ul>

                  <h3 className="wp-block-heading" data-block-type="core">
                    <strong>How does teledentistry help me?</strong>
                  </h3>

                  <ul data-block-type="core">
                    <li data-block-type="core">● No need to visit the dental office in person.</li>

                    <li data-block-type="core">● You can reduce the risk of getting sick from other people.</li>

                    <li data-block-type="core">● You can find out if you need to go to the office for more care.</li>

                    <li data-block-type="core">● You get help from a care coordinator if you need in-person visits.</li>

                    <li data-block-type="core">● You can learn ways to improve your oral health.</li>
                  </ul>

                  <h3 className="wp-block-heading" data-block-type="core">
                    <strong>Can teledentistry be bad for me?</strong>&nbsp;
                  </h3>

                  <ul data-block-type="core">
                    <li data-block-type="core">
                      ● It might feel different since you won't be in the same room as the dentist.
                    </li>

                    <li data-block-type="core">
                      ● The dentist can’t take X-rays during a teledentistry visit, which may limit diagnoses.
                    </li>
                    <li data-block-type="core">● You may still need an office visit.</li>

                    <li data-block-type="core">● Technical issues may interrupt your appointment.</li>
                  </ul>

                  <h3 className="wp-block-heading" data-block-type="core">
                    <strong>Will my teledentistry visit be private?</strong>
                  </h3>

                  <ul data-block-type="core">
                    <li data-block-type="core">
                      ● We won’t record visits, but during the visit, your provider may take video or photos for your
                      health record.
                    </li>

                    <li data-block-type="core">
                      ● Make sure you’re in a private place so others can’t overhear your conversation.
                    </li>

                    <li data-block-type="core">● The dentist will let you know if someone may hear or see you.</li>

                    <li data-block-type="core">
                      ● Dentistry.One’s telehealth technology is designed to keep your information safe.
                    </li>

                    <li data-block-type="core">● Use a secure Internet connection for your visit.</li>

                    <li data-block-type="core">
                      ● There is a small chance that someone could listen in, but we take steps to protect your privacy.
                    </li>

                    <li data-block-type="core">
                      ● To help with your care, we might share your health information with other healthcare providers.
                    </li>

                    <li data-block-type="core">
                      ● You can read our{' '}
                      <a
                        style={{ fontSize: 'inherit', color: '#2f64c1', textDecoration: 'none' }}
                        href={`${window.location.origin}/privacy-policy`}
                      >
                        Privacy Policy
                      </a>{' '}
                      at <a href={`${window.location.origin}/privacy-policy`}>https://dentistry.one/privacy-policy</a>.
                    </li>
                  </ul>

                  <h3 className="wp-block-heading" data-block-type="core">
                    <strong>What if I want an office visit instead?</strong>
                  </h3>

                  <ul data-block-type="core">
                    <li data-block-type="core">
                      ● Dentistry.One provides virtual care, so we don't have physical dental offices, but we can help
                      you find one.
                    </li>
                  </ul>

                  <h3 className="wp-block-heading" data-block-type="core">
                    <strong>What if I try teledentistry and don’t like it?</strong>
                  </h3>

                  <ul data-block-type="core">
                    <li data-block-type="core">
                      ● You can stop using teledentistry at any time, even during your appointment.
                    </li>

                    <li data-block-type="core">
                      ● If you prefer, you can still get an office visit, and our Care Coordination team can help you.
                    </li>
                  </ul>

                  <h3 className="wp-block-heading" data-block-type="core">
                    <strong>How much does a telehealth visit cost?&nbsp;</strong>
                  </h3>

                  <ul data-block-type="core">
                    <li data-block-type="core">● If you have insurance, your cost depends on your plan.</li>

                    <li data-block-type="core">
                      ● A teledentistry visit will not cost more than a regular office visit.
                    </li>

                    <li data-block-type="core">
                      ● If the dentist decides you need to visit the office after your online visit, you may have to pay
                      for both.
                    </li>
                  </ul>

                  <h3 className="wp-block-heading" data-block-type="core">
                    <strong>What digital communication does Dentistry.One use?</strong>
                  </h3>

                  <ul data-block-type="core">
                    <li data-block-type="core">
                      ● Dentistry.One communicates through mobile phone, messages, e-mail, and online chats.
                    </li>

                    <li data-block-type="core">
                      ● All communications follow privacy regulations in our&nbsp;
                      <a style={{ fontSize: 'inherit' }} href={`${window.location.origin}/privacy-policy`}>
                        Privacy Policy
                      </a>{' '}
                      and Notice of &nbsp;
                      <a href={`${window.location.origin}/notice-of-privacy-practices `}>Privacy Practices</a>.&nbsp;
                    </li>

                    <li data-block-type="core">
                      ● We may also get your contact information through your employer or others who work with
                      Dentistry. One is to provide teledentistry services.
                    </li>

                    <li data-block-type="core">
                      ● By accepting, you agree to the terms of Dentistry.One can communicate this way and understand
                      that safeguards are in place; the content could be intercepted by others or not be encrypted.
                    </li>

                    <li data-block-type="core">
                      ● If you decline, the use of Dentistry.One will not be available to you.
                    </li>
                  </ul>

                  <h3 className="wp-block-heading" data-block-type="core">
                    <strong>What are my rights as a patient?</strong>
                  </h3>

                  <ul data-block-type="core">
                    <li data-block-type="core">
                      ● You have the right to ask your provider questions about the risks, benefits, and alternatives to
                      teledentistry visits and to know the provider’s licensure and qualifications.
                    </li>

                    <li data-block-type="core">
                      ● You have the right to receive interactive, real-time communications with the dental provider(s)
                      providing your teledentistry visit, both at the time and within sixteen (16) days after your
                      teledentistry visit.
                    </li>

                    <li data-block-type="core">
                      ● You can change your mind and withdraw your consent for a teledentistry visit at any time before
                      or during the visit.
                    </li>

                    <li data-block-type="core">
                      ● For more details on your rights and privacy please consult our{' '}
                      <a href={`${window.location.origin}/privacy-policy`}>Privacy Policy</a> and &nbsp;
                      <a style={{ fontSize: 'inherit' }} href={`${window.location.origin}/notice-of-privacy-practices`}>
                        Notice of Privacy Policy
                      </a>{' '}
                      .
                    </li>
                  </ul>

                  <h3 className="wp-block-heading" data-block-type="core">
                    <strong>Do I have to accept this document?</strong>
                  </h3>

                  <ul data-block-type="core">
                    <li data-block-type="core">● No. Only accept this document if you want to use teledentistry.</li>
                  </ul>

                  <h3 className="wp-block-heading" data-block-type="core">
                    <strong>What does it mean if I accept?</strong>
                  </h3>

                  <p>If you accept, you agree that:</p>
                  <ul data-block-type="core">
                    <li data-block-type="core">● I have read and understand this document.</li>
                    <li data-block-type="core">● You consent to participate in a teledentistry visit.</li>
                    <li data-block-type="core">
                      ● If you consent for a minor, you confirm you are their legal parent or guardian and have the
                      right to give consent.
                    </li>
                  </ul>
                </div>
              </article>
            </main>
          </div>
        </div>
      </Container>
    </>
  );
};

export default ConsentPolicy;
